import styled from 'styled-components'

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  font-size: 1em;
  font-family: 'Arial', sans-serif;
  // @todo disable the box shadow
  //box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);

  // break long URLs and text to prevent overflow
  p > a {
    overflow-wrap: break-word;
    word-break: break-all;
  }
`

const TableHead = styled.thead`
  background-color: var(--main-color);
  color: #ffffff;
  text-align: left;
`

const TableRow = styled.tr`
  border-bottom: 1px solid #dddddd;

  &:nth-of-type(even) {
    background-color: #f3f3f3;
  }

  &:last-of-type {
    // @todo disable the border at the last row
    //border-bottom: 1px solid var(--main-color);
  }
`
// @todo unused
const TableHeader = styled.th`
  padding: 12px 15px;
`

const TableCell = styled.td`
  padding: 12px 15px;
`

/**
 * Renders a table of attributes.
 *
 * It takes an `attributes` prop, which is an object containing the attribute data.
 * The component formats the attribute values by replacing line breaks with
 * paragraph tags, URLs with anchor tags, and list items with unordered lists.
 *
 * @todo replace dangerouslySetInnerHTML with a safer alternative to sanitize HTML
 * @param {Object} props - The component props.
 * @param {Object} props.attributes - The attribute data.
 * @returns {JSX.Element} The rendered table component.
 */
const AttributeTable = ({ attributes }) => {
  if (!attributes || typeof attributes !== 'object') {
    return null // handle case where attributes is null or not an object
  }

  const formatTextToHTML = (text) => {
    // First handle URLs and anchor tags before dealing with paragraphs
    const textWithLinks = text.replace(
      /(<a\s+[^>]*>.*?<\/a>)|(https?:\/\/[^\s]+)(\s+\(https?:\/\/[^\s]+\))?/g,
      (match, existingAnchor, url1, url2) => {
        // If it's an existing anchor tag, return it as is
        if (existingAnchor) {
          return existingAnchor;
        }
  
        // Handle URLs with parentheses
        if (url2) {
          // Remove the duplicated URL in parentheses
          return `<a href="${url1}" target="_blank">${url1}</a>`;
        } else {
          // Handle normal URLs and URLs with text descriptions
          return `<a href="${url1}" target="_blank">${match}</a>`;
        }
      }
    );
  
    // Then handle paragraphs - only wrap in <p> tags if there are actual paragraph breaks
    const formattedText = textWithLinks
      .split('\n\n')
      .map((paragraph) => {
        const trimmed = paragraph.trim();
        // Only wrap in <p> tags if it's not just a single URL/anchor tag
        if (trimmed.startsWith('<a') && trimmed.endsWith('</a>')) {
          return trimmed;
        }
        return `<p>${trimmed}</p>`;
      })
      .join('');
  
    // Replace list items
    const formattedWithLists = formattedText
      .replace(/(\* .+?)(?=\n|<\/p>)/g, (match) => `<li>${match.slice(2)}</li>`)
      .replace(/<p>(<li>.+<\/li>)<\/p>/g, '<ul>$1</ul>');
  
    return formattedWithLists;
  };
  

  const renderRows = () => {
    return Object.keys(attributes).map((key) => {
      const value = attributes[key]?.StringValue || '' // Fallback to empty string if value is missing
      return (
        <TableRow key={key}>
          <TableCell>{key}</TableCell>
          <TableCell
            dangerouslySetInnerHTML={{ __html: formatTextToHTML(value) }}
          />
        </TableRow>
      )
    })
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Value</TableCell>
        </TableRow>
      </TableHead>
      <tbody>{renderRows()}</tbody>
    </Table>
  )
}

export default AttributeTable
